import pluralize from "pluralize"
import React, { useContext, useEffect, useRef } from "react"

import Skeleton from "src/components/Skeleton"

import {
  getContractQuoteSelectableIds,
  getContractQuotes,
} from "src/api/Contracts"

import useDatatable from "src/hooks/use_datatable"

import ContractTabs from "./ContractTabs"
import { ContractGroupContext } from "./index"

const TABS = ["unsent", "outstanding", "completed"]
const PROCESSING_POLL_MS = 5000

const Contracts = () => {
  const { group } = useContext(ContractGroupContext)
  const initialTabSet = useRef(false)

  const table = useDatatable({
    queryKey: ["contractQuotes", group.encodedId],
    fetchFn: (params) => getContractQuotes(group.encodedId, params),
    defaultFilters: {
      tab: "unsent",
      search: "",
      statuses: [],
      inMarinaStartDate: null,
      inMarinaEndDate: null,
      linked: "all",
    },
    fetchAllIds: (params) =>
      getContractQuoteSelectableIds(group.encodedId, params).then((r) => r.ids),
    idKey: "encodedId",
    refetchInterval: (data) =>
      data?.data?.some((row) => row.status === "Processing")
        ? PROCESSING_POLL_MS
        : false,
  })

  const tabCounts = table.data?.filterOptions?.tabs
  const totalCount = tabCounts
    ? (tabCounts.unsent || 0) +
      (tabCounts.outstanding || 0) +
      (tabCounts.completed || 0)
    : 0

  const currentTab = table.filters.tab
  const { setFilter } = table

  useEffect(() => {
    if (!tabCounts || initialTabSet.current) return
    initialTabSet.current = true

    const currentTabCount = tabCounts[currentTab] || 0

    if (currentTabCount > 0) return

    const firstNonEmpty = TABS.find((t) => (tabCounts[t] || 0) > 0)
    if (firstNonEmpty && firstNonEmpty !== currentTab) {
      setFilter("tab", firstNonEmpty)
    }
  }, [tabCounts, currentTab, setFilter])

  if (table.isError) {
    return (
      <div className="text-red-600">
        There was an error loading your contracts. Please reload the page to try
        again.
      </div>
    )
  }

  return (
    <>
      <h2 className="my-4 ml-2 text-base font-semibold print:hidden">
        {tabCounts ? (
          <>
            {totalCount} Total {pluralize("Contract", totalCount)}
          </>
        ) : (
          <Skeleton.Bar width="w-48" height="h-5" />
        )}
      </h2>
      <ContractTabs tabCounts={tabCounts} table={table} />
    </>
  )
}

export default Contracts
